// hooks/useBusinesses.js
// BuzzInApp — Lista de comercios desde Google Sheet, ordenada por distancia
//
// Flujo:
//   1. Fetch CSV de Google Sheet (businesses)
//   2. Parseo → array de objetos
//   3. Si hay ubicación del usuario → calcula distancia a cada comercio
//   4. Ordena: featured primero, luego por distancia
//   5. Refresca cada REFRESH_INTERVAL_MS (sin spam de requests)

import { useState, useEffect, useCallback, useRef } from "react";
import { haversineDistance } from "../security/geoFence";

// ─── Configuración ────────────────────────────────────────────────────────────
const SHEET_URL =
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vQEnqbU7fsacNs6NGKRWDdNhWA-Xr1U4rWuD8rr4gUlm2NLSSSQHFbdzbwwIZm5p0eW5RiCecQtHPw8/pub?gid=0&single=true&output=csv";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos

// Columnas esperadas del Sheet (en orden)
// id, name, type, address, lat, lng, distance_mi, plan,
// icon, image_url, prize_user, prize_clicks_needed, prize_friends,
// featured, active, weekly_sharers, notes
//
// image_url: URL pública de la imagen/logo del comercio (opcional).
//            Si está vacía, BizCard usa el emoji de icon o placeholder-biz.png.
// Columnas del Sheet — referencia para el city partner al configurar la hoja
// ["id","name","type","address","lat","lng","distance_mi","plan",
//  "icon","image_url","prize_user","prize_clicks_needed","prize_friends",
//  "featured","active","weekly_sharers","notes"]

// ─── Parser de CSV ────────────────────────────────────────────────────────────

/**
 * Parsea el CSV de Google Sheet y devuelve un array de objetos.
 * Maneja campos con comas dentro de comillas.
 * @param {string} csvText
 * @returns {object[]}
 */
const parseCSV = (csvText) => {
  const lines = csvText.trim().split("\n");
  if (lines.length < 2) return [];

  // Primera línea = headers (los usamos para hacer el map dinámico)
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

  return lines.slice(1).map((line) => {
    // Split respetando comillas
    const values = [];
    let current  = "";
    let inQuotes = false;

    for (const char of line) {
      if (char === '"')      { inQuotes = !inQuotes; continue; }
      if (char === "," && !inQuotes) { values.push(current.trim()); current = ""; continue; }
      current += char;
    }
    values.push(current.trim());

    const obj = {};
    headers.forEach((h, i) => {
      obj[h] = values[i] ?? "";
    });

    // Normalizar tipos
    return {
      ...obj,
      lat:                parseFloat(obj.lat)               || null,
      lng:                parseFloat(obj.lng)               || null,
      prize_clicks_needed:parseInt(obj.prize_clicks_needed) || 1,
      featured:           obj.featured?.toLowerCase() === "true",
      active:             obj.active?.toLowerCase()   !== "false", // activo por defecto
      weekly_sharers:     parseInt(obj.weekly_sharers)      || 0,
      image_url:          obj.image_url?.trim() || null,
    };
  }).filter((b) => b.active && b.id); // solo comercios activos con ID
};

// ─── Calcular distancias ──────────────────────────────────────────────────────

/**
 * Agrega la distancia en metros a cada comercio y ordena el array.
 * @param {object[]} businesses
 * @param {{ lat: number, lng: number } | null} userLocation
 * @returns {object[]}
 */
const withDistances = (businesses, userLocation) => {
  return businesses
    .map((b) => {
      if (!userLocation || !b.lat || !b.lng) {
        return { ...b, distanceMeters: null };
      }
      const distanceMeters = Math.round(
        haversineDistance(userLocation.lat, userLocation.lng, b.lat, b.lng)
      );
      return { ...b, distanceMeters };
    })
    .sort((a, b) => {
      // Featured primero
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured)  return 1;

      // Luego por distancia (null al final)
      if (a.distanceMeters === null && b.distanceMeters === null) return 0;
      if (a.distanceMeters === null) return 1;
      if (b.distanceMeters === null) return -1;
      return a.distanceMeters - b.distanceMeters;
    });
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
const useBusinesses = (userLocation = null) => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const rawRef                      = useRef([]); // cache sin distancias

  // ─── Fetch desde Google Sheet ──────────────────────────────────────────
  const fetchBusinesses = useCallback(async () => {
    try {
      setError(null);
      const res  = await fetch(SHEET_URL);
      if (!res.ok) throw new Error(`Sheet fetch error: ${res.status}`);
      const text = await res.text();
      const raw  = parseCSV(text);
      rawRef.current = raw;
      setLastFetched(Date.now());
      return raw;
    } catch (err) {
      setError("sheet_fetch_error");
      console.error("[useBusinesses] fetch error:", err);
      return null;
    }
  }, []);

  // ─── Carga inicial ─────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const raw = await fetchBusinesses();
      if (!cancelled && raw) {
        setBusinesses(withDistances(raw, userLocation));
      }
      if (!cancelled) setLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Actualizar distancias cuando cambia la ubicación del usuario ──────
  useEffect(() => {
    if (rawRef.current.length > 0) {
      setBusinesses(withDistances(rawRef.current, userLocation));
    }
  }, [userLocation?.lat, userLocation?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Auto-refresh cada 5 minutos ───────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(async () => {
      const raw = await fetchBusinesses();
      if (raw) {
        setBusinesses(withDistances(raw, userLocation));
      }
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchBusinesses, userLocation]);

  // ─── Helpers ───────────────────────────────────────────────────────────

  /** Busca un comercio por ID */
  const getById = useCallback(
    (id) => businesses.find((b) => b.id === id) ?? null,
    [businesses]
  );

  /** Filtra por tipo (restaurante, gimnasio, etc.) */
  const filterByType = useCallback(
    (type) =>
      type ? businesses.filter((b) => b.type?.toLowerCase() === type.toLowerCase()) : businesses,
    [businesses]
  );

  /** Búsqueda por nombre */
  const search = useCallback(
    (query) => {
      if (!query) return businesses;
      const q = query.toLowerCase();
      return businesses.filter(
        (b) =>
          b.name?.toLowerCase().includes(q) ||
          b.type?.toLowerCase().includes(q) ||
          b.address?.toLowerCase().includes(q)
      );
    },
    [businesses]
  );

  return {
    businesses,
    loading,
    error,
    lastFetched,
    refresh: fetchBusinesses,
    getById,
    filterByType,
    search,
  };
};

export default useBusinesses;
